import {
  defaultLabels,
  defaultShape,
  shapeQuestions,
  type BookingFor,
  type BusinessLabels,
  type BusinessShape,
  type ClassFill,
  type ShapeQuestion,
  type Sport,
} from "@teamna/shared";
import { useState, type FormEvent } from "react";
import { useOwnerBusiness } from "@teamna/web-client/hooks/use-owner.ts";
import { useUpdateBusiness } from "@teamna/web-client/hooks/use-setup.ts";
import { Button, ChipRow, ErrorState, FormSection, InlineError, RadioGroup, SkeletonList, showToast } from "../components/index.ts";
import { labelPreviewLine, SPORT_OPTIONS } from "../lib/sports.ts";
import { SaveBar } from "./setup/frame.tsx";

/**
 * "How your business works" (Pilates studio shape epic, feature F1, migration 0069): the two
 * switches that decide how Teamna behaves. Who books (`bookingFor`) and how classes fill
 * (`classFill`). The sport only gives the first answers.
 *
 * - `ShapeChoices`: the two questions as choice cards, each with one preview line. No data.
 * - `BusinessShapeForm`: the cards with a Save button, for Settings > Business. No data.
 * - `BusinessShapeSection`: the form with the owner business and the save.
 * - `SportShapeFields`: the sport chips, then the shape filled from the sport, for the sign-up.
 */

export const SHAPE_TITLE = "How your business works";

/** The two questions, side by side from 640 px. The words come from the business's labels. */
export function ShapeChoices({
  value,
  labels,
  onChange,
}: {
  value: BusinessShape;
  labels: BusinessLabels;
  onChange: (next: BusinessShape) => void;
}) {
  const questions = shapeQuestions(labels);
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <ChoiceCard question={questions.bookingFor} value={value.bookingFor} onChange={(bookingFor: BookingFor) => onChange({ ...value, bookingFor })} />
      <ChoiceCard question={questions.classFill} value={value.classFill} onChange={(classFill: ClassFill) => onChange({ ...value, classFill })} />
    </div>
  );
}

function ChoiceCard<T extends string>({ question, value, onChange }: { question: ShapeQuestion<T>; value: T; onChange: (value: T) => void }) {
  return (
    <div className="flex flex-col gap-2 rounded-xl border border-line bg-paper p-4">
      <RadioGroup legend={question.title} options={question.options} value={value} onChange={onChange} />
      <p className="text-[13px] text-ink-2" aria-live="polite">
        {question.preview(value)}
      </p>
    </div>
  );
}

/**
 * The Settings form. `saved` is the shape the business has now; Save turns on when a choice
 * differs from it. `initialDraft` opens the form with a change already made (Storybook).
 */
export function BusinessShapeForm({
  saved,
  labels,
  saving = false,
  error = null,
  onSave,
  initialDraft,
}: {
  saved: BusinessShape;
  labels: BusinessLabels;
  saving?: boolean;
  error?: unknown;
  onSave: (shape: BusinessShape) => void;
  initialDraft?: BusinessShape;
}) {
  const [draft, setDraft] = useState<BusinessShape>(initialDraft ?? saved);
  const changed = draft.bookingFor !== saved.bookingFor || draft.classFill !== saved.classFill;

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (changed) onSave(draft);
  }

  return (
    <form onSubmit={submit}>
      <FormSection
        title={SHAPE_TITLE}
        description="Two choices decide what Teamna shows. Your sport gave the first answers. You can change them at any time."
      >
        <div className="py-4">
          <ShapeChoices value={draft} labels={labels} onChange={setDraft} />
        </div>
      </FormSection>
      {error ? (
        <div className="mt-4">
          <InlineError error={error} />
        </div>
      ) : null}
      {/* Save is off until an answer changes. The note says that the answers on the page are already
          saved, so an owner who only wants to accept them knows that there is nothing to do. */}
      <SaveBar note={changed ? undefined : "These answers are saved. Choose a different answer to change them."}>
        <Button type="submit" disabled={!changed} loading={saving}>
          Save
        </Button>
      </SaveBar>
    </form>
  );
}

/** Settings > Business: the owner business's shape, saved with `PATCH /owner/businesses/:id`. */
export function BusinessShapeSection({ businessId }: { businessId: string }) {
  const business = useOwnerBusiness(businessId);
  const update = useUpdateBusiness(businessId);
  if (business.isLoading) return <SkeletonList rows={2} />;
  if (business.error || !business.data) {
    return <ErrorState error={business.error ?? new Error("missing")} onRetry={() => void business.refetch()} />;
  }
  const saved: BusinessShape = { bookingFor: business.data.bookingFor, classFill: business.data.classFill };
  return (
    <BusinessShapeForm
      // A new saved shape starts a new form, so the draft never shows an old answer.
      key={`${saved.bookingFor}:${saved.classFill}`}
      saved={saved}
      labels={business.data.labels}
      saving={update.isPending}
      error={update.error}
      onSave={(shape) => update.mutate(shape, { onSuccess: () => showToast("Saved. Teamna now works this way.") })}
    />
  );
}

/**
 * The sport, then how the business works, on the owner sign-up. No sport is picked first (O1): the
 * owner picks it, so a studio never starts with swimming words by mistake. A sport fills both shape
 * answers with its start shape (`defaultShape`), and the words come from the sport's start labels.
 * Until a sport is picked, the shape questions do not show. `error` shows under the chips.
 */
export function SportShapeFields({
  sport,
  shape,
  error,
  onChange,
}: {
  sport: Sport | null;
  shape: BusinessShape | null;
  error?: string;
  onChange: (next: { sport: Sport; shape: BusinessShape }) => void;
}) {
  return (
    <>
      <div className="flex flex-col gap-2">
        <p className="eyebrow text-ink-2">Sport</p>
        <ChipRow
          label="Sport"
          wrap
          options={SPORT_OPTIONS}
          selectedId={sport ?? ""}
          onSelect={(id) => onChange({ sport: id as Sport, shape: defaultShape(id as Sport) })}
        />
        {error ? (
          <p role="alert" className="text-[13px] font-medium text-cancelled">
            {error}
          </p>
        ) : null}
        <p className="text-[14px] text-ink-2" aria-live="polite">
          {sport ? labelPreviewLine(sport) : "Choose your sport. Teamna uses its words and its first settings. You can change them later."}
        </p>
      </div>
      {sport && shape ? (
        <div className="flex flex-col gap-2">
          <p className="eyebrow text-ink-2">{SHAPE_TITLE}</p>
          <p className="text-[13px] text-ink-2">We filled these from your sport. Change them if your business works another way.</p>
          <ShapeChoices value={shape} labels={defaultLabels(sport)} onChange={(next) => onChange({ sport, shape: next })} />
        </div>
      ) : null}
    </>
  );
}
